import { useEffect, useState } from 'react';
import { Loader2, Megaphone } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabaseClient';
import { invokeErrorMessage } from '../../lib/supabaseFunctionError';
import type { SocialPlatform } from '../SocialConnectButton';

type AdAccount = { account_id: string; name: string | null };

const GOALS = [
  { id: 'engagement', label: 'Engagement' },
  { id: 'interactions', label: 'Likes and comments' },
  { id: 'awareness_views', label: 'Awareness, more views' },
  { id: 'awareness_audience', label: 'Awareness, more people' },
  { id: 'traffic', label: 'Traffic to a link' },
] as const;

/**
 * Boost a post that is already live. The button on this form is the confirmation.
 * The money comes from the customer's Meta ad account.
 */
export function BoostPostForm({
  businessId,
  platform,
  connectionId,
  postId,
  ayrsharePostId,
}: {
  businessId: string;
  platform: SocialPlatform;
  connectionId: string | undefined;
  postId: string;
  ayrsharePostId: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState<AdAccount[]>([]);
  const [accountId, setAccountId] = useState('');
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountNote, setAccountNote] = useState<string | null>(null);
  const [goal, setGoal] = useState<string>('engagement');
  const [budget, setBudget] = useState('20');
  const [days, setDays] = useState('7');
  const [minAge, setMinAge] = useState('25');
  const [maxAge, setMaxAge] = useState('55');
  const [gender, setGender] = useState('all');
  const [country, setCountry] = useState('US');
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !businessId) return;
    let cancelled = false;
    setLoadingAccounts(true);
    setAccountNote(null);
    void (async () => {
      const { data, error } = await supabase.functions.invoke('ayrshare-social-ads', {
        body: { action: 'accounts', businessId, platform, connectionId },
      });
      if (cancelled) return;
      setLoadingAccounts(false);
      if (error || data?.error) {
        setAccountNote((await invokeErrorMessage(error, data)) || data?.error || 'Could not load ad accounts');
        setAccounts([]);
        return;
      }
      const rows = (Array.isArray(data?.accounts) ? data.accounts : []) as AdAccount[];
      setAccounts(rows);
      setAccountId(rows[0]?.account_id || '');
      if (!rows.length) setAccountNote(data?.note || 'No Meta ad account is connected.');
    })();
    return () => { cancelled = true; };
  }, [open, businessId, platform, connectionId]);

  if (platform === 'linkedin') return null;

  const submit = async () => {
    const amount = Number(budget);
    const runDays = Number(days);
    if (!accountId) {
      toast.error('Choose a Meta ad account first');
      return;
    }
    if (!Number.isFinite(amount) || amount < 1 || amount > 1000) {
      toast.error('Budget is $1 to $1,000');
      return;
    }
    if (!Number.isFinite(runDays) || runDays < 2) {
      toast.error('Meta needs the boost to run at least 2 days');
      return;
    }
    const start = new Date();
    const end = new Date(start.getTime() + runDays * 86_400_000);
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke('ayrshare-social-ads', {
        body: {
          action: 'boost',
          businessId,
          platform,
          connectionId,
          postId,
          ayrsharePostId,
          accountId,
          goal,
          budgetType: 'daily',
          budgetAmount: amount,
          bidAmount: 1,
          startAt: start.toISOString(),
          endAt: end.toISOString(),
          minAge: Number(minAge) || 18,
          maxAge: Number(maxAge) || 65,
          gender,
          country: country.trim().toUpperCase() || 'US',
          link: goal === 'traffic' ? link.trim() : '',
          button: goal === 'traffic' && link.trim() ? 'LEARN_MORE' : '',
          adName: 'Boost',
        },
      });
      if (error || data?.error) {
        toast.error((await invokeErrorMessage(error, data)) || data?.error || 'The boost was not accepted');
        return;
      }
      toast.success(data?.note || 'Boost sent to Meta');
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-3 inline-flex items-center gap-1.5 rounded-[10px] border border-gray-200 px-3 py-2 text-[13px] font-semibold text-gray-800 active:bg-gray-50"
      >
        <Megaphone className="h-3.5 w-3.5" />
        Boost this post
      </button>
    );
  }

  return (
    <div className="mt-3 rounded-xl border border-gray-200 p-3 space-y-2.5">
      <div className="flex items-center justify-between">
        <p className="text-[13px] font-semibold text-gray-900">Boost this post</p>
        <button type="button" onClick={() => setOpen(false)} className="text-[12px] font-medium text-gray-500">Cancel</button>
      </div>
      <p className="text-[12px] text-gray-500">
        This spends from your Meta ad account, not Nexus. Meta reviews it before it runs. It has to run at least 30 hours.
      </p>
      {loadingAccounts ? (
        <p className="inline-flex items-center gap-1.5 text-[12px] text-gray-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking ad accounts</p>
      ) : accountNote && accounts.length === 0 ? (
        <p className="rounded-lg bg-amber-50 px-2.5 py-2 text-[12px] text-amber-800">{accountNote}</p>
      ) : accounts.length > 1 ? (
        <label className="block text-[12px] text-gray-600">
          Ad account
          <select value={accountId} onChange={(e) => setAccountId(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-[13px] text-gray-900">
            {accounts.map((row) => (
              <option key={row.account_id} value={row.account_id}>{row.name || row.account_id}</option>
            ))}
          </select>
        </label>
      ) : null}
      <label className="block text-[12px] text-gray-600">
        Goal
        <select value={goal} onChange={(e) => setGoal(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-[13px] text-gray-900">
          {GOALS.map((row) => <option key={row.id} value={row.id}>{row.label}</option>)}
        </select>
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className="block text-[12px] text-gray-600">
          $ per day
          <input value={budget} onChange={(e) => setBudget(e.target.value)} inputMode="decimal" className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-[13px] text-gray-900" />
        </label>
        <label className="block text-[12px] text-gray-600">
          Days
          <input value={days} onChange={(e) => setDays(e.target.value)} inputMode="numeric" className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-[13px] text-gray-900" />
        </label>
        <label className="block text-[12px] text-gray-600">
          Age from
          <input value={minAge} onChange={(e) => setMinAge(e.target.value)} inputMode="numeric" className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-[13px] text-gray-900" />
        </label>
        <label className="block text-[12px] text-gray-600">
          Age to
          <input value={maxAge} onChange={(e) => setMaxAge(e.target.value)} inputMode="numeric" className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-[13px] text-gray-900" />
        </label>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="block text-[12px] text-gray-600">
          Gender
          <select value={gender} onChange={(e) => setGender(e.target.value)} className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-[13px] text-gray-900">
            <option value="all">Everyone</option>
            <option value="female">Women</option>
            <option value="male">Men</option>
          </select>
        </label>
        <label className="block text-[12px] text-gray-600">
          Country
          <input value={country} onChange={(e) => setCountry(e.target.value)} maxLength={2} className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-[13px] uppercase text-gray-900" />
        </label>
      </div>
      {goal === 'traffic' ? (
        <label className="block text-[12px] text-gray-600">
          Link
          <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://" className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-[13px] text-gray-900" />
        </label>
      ) : null}
      <button
        type="button"
        onClick={() => void submit()}
        disabled={busy || !accountId}
        className="inline-flex w-full items-center justify-center gap-1.5 rounded-[10px] bg-gray-900 px-3 py-2.5 text-[13px] font-semibold text-white disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Megaphone className="h-3.5 w-3.5" />}
        Boost for ${budget || '0'} a day
      </button>
    </div>
  );
}
